/* iter-220 · Hero Participant Slots + Quick-Add Sheet.
 *
 * Lightweight participant assignment for the Deal Room hero card.
 * Five canonical slots — Seller, Buyer, Seller Solicitor, Buyer
 * Solicitor, Mortgage Broker. Each slot renders compact and
 * calm: empty → "+ Add seller" CTA; populated → name + role
 * label. Clicking either opens the quick-add sheet which lets
 * the operator drop in {full_name, email, phone?} in <5 seconds.
 *
 * NOT a CRM. No permissions UI. No avatar grids. No editing of
 * existing participants (that lives inside the canonical
 * Participants tab). Pilot scope only.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, User } from "lucide-react";

/* Canonical five slots — render order matches the operational
 * sequence of a transaction (sell-side first, then buy-side, then
 * the broker chain). */
export const HERO_PARTICIPANT_SLOTS = [
  { type: "seller",            label: "Seller",            cta: "Add seller"          },
  { type: "buyer",             label: "Buyer",             cta: "Add buyer"           },
  { type: "seller_solicitor",  label: "Seller solicitor",  cta: "Add seller solicitor"},
  { type: "buyer_solicitor",   label: "Buyer solicitor",   cta: "Add buyer solicitor" },
  { type: "mortgage_broker",   label: "Mortgage broker",   cta: "Add mortgage broker" },
];

function pickName(p) {
  return p?.full_name || (p?.email ? p.email.split("@")[0] : null);
}

function findBySlot(participants, type) {
  if (!Array.isArray(participants)) return null;
  return participants.find((p) => p && p.participant_type === type) || null;
}

/* ─── Hero panel ─────────────────────────────────────────────────── */
export function HeroParticipantsPanel({
  participants,
  onAdd,           // async (slotType, payload) → POST
  onJumpToTab,     // open the Participants tab for advanced edits
  disabled = false,
}) {
  const [sheetSlot, setSheetSlot] = useState(null);
  return (
    <div className="mt-3.5" data-testid="hero-participants-panel">
      <div className="flex flex-wrap gap-2.5" data-testid="hero-participants-grid">
        {HERO_PARTICIPANT_SLOTS.map((slot) => {
          const existing = findBySlot(participants, slot.type);
          return (
            <ParticipantSlot
              key={slot.type}
              slot={slot}
              existing={existing}
              onAddClick={() => setSheetSlot(slot)}
              onJumpToTab={onJumpToTab}
              disabled={disabled}
            />
          );
        })}
      </div>
      <AddParticipantSheet
        slot={sheetSlot}
        onClose={() => setSheetSlot(null)}
        onSubmit={async (payload) => {
          await onAdd(sheetSlot.type, payload);
          setSheetSlot(null);
        }}
      />
    </div>
  );
}

/* ─── Slot ───────────────────────────────────────────────────────── */
function ParticipantSlot({ slot, existing, onAddClick, onJumpToTab, disabled }) {
  const name = pickName(existing);
  const tid  = `hero-participant-slot-${slot.type}`;
  if (name) {
    return (
      <button
        type="button"
        onClick={onJumpToTab}
        className="group inline-flex items-center gap-2 h-10 rounded-xl bg-slate-50/70 hover:bg-white ring-1 ring-slate-200/70 hover:ring-violet-200 px-3 transition-colors"
        data-testid={tid}
        data-state="populated"
        title={`Open participants — ${slot.label}`}
      >
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-50 text-violet-700 text-[10px] font-bold ring-1 ring-violet-100"
          aria-hidden
        >
          {(name[0] || "?").toUpperCase()}
        </span>
        <span className="flex flex-col items-start leading-tight min-w-0">
          <span className="text-[9.5px] uppercase tracking-[0.10em] text-slate-400 font-semibold">
            {slot.label}
          </span>
          <span
            className="text-[12.5px] font-semibold text-slate-900 truncate max-w-[160px]"
            data-testid={`${tid}-name`}
          >
            {name}
          </span>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onAddClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 h-10 rounded-xl bg-white hover:bg-violet-50/40 ring-1 ring-dashed ring-slate-200 hover:ring-violet-300 px-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid={tid}
      data-state="empty"
    >
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-50 text-slate-400 ring-1 ring-slate-200">
        <User size={12} strokeWidth={2.2} />
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[9.5px] uppercase tracking-[0.10em] text-slate-400 font-semibold">
          {slot.label}
        </span>
        <span className="text-[12.5px] font-semibold text-violet-700">
          + {slot.cta}
        </span>
      </span>
    </button>
  );
}

/* ─── Quick-add sheet (portal modal) ─────────────────────────────── */
function AddParticipantSheet({ slot, onClose, onSubmit }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const inputRef = useRef(null);

  // Reset on open.
  useEffect(() => {
    if (slot) {
      setForm({ full_name: "", email: "", phone: "" });
      setError(null);
      setBusy(false);
      // Focus first input after mount.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [slot]);

  // Esc to close.
  useEffect(() => {
    if (!slot) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slot, busy, onClose]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!form.full_name.trim()) {
      setError("Full name is required");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        full_name: form.full_name.trim(),
        email:     form.email.trim() || null,
        phone:     form.phone.trim() || null,
      });
    } catch (err) {
      setBusy(false);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err?.message || "Couldn't add participant"));
    }
  }, [busy, form, onSubmit]);

  if (!slot) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hero-add-participant-title"
      data-testid="hero-add-participant-sheet"
    >
      <button
        type="button"
        aria-label="Close add participant sheet"
        onClick={busy ? undefined : onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
        data-testid="hero-add-participant-overlay"
      />
      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-[420px] rounded-2xl bg-white p-5 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.30)] ring-1 ring-slate-200"
      >
        <header className="flex items-start justify-between mb-1">
          <div>
            <h2
              id="hero-add-participant-title"
              className="text-[15px] font-bold text-slate-900 tracking-tight leading-tight"
            >
              Add {slot.label.toLowerCase()}
            </h2>
            <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
              Just the basics — you can fill in the rest later from the Participants tab.
            </p>
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            className="inline-flex w-7 h-7 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            aria-label="Close"
            data-testid="hero-add-participant-close"
          >
            <X size={14} />
          </button>
        </header>

        <div className="mt-4 space-y-3">
          <Field
            label="Full name"
            required
            value={form.full_name}
            onChange={(v) => setForm((f) => ({ ...f, full_name: v }))}
            placeholder="e.g. Olivia Bennett"
            testid="hero-add-participant-name"
            inputRef={inputRef}
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            placeholder="olivia@example.com"
            testid="hero-add-participant-email"
          />
          <Field
            label="Phone"
            type="tel"
            value={form.phone}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
            placeholder="(optional)"
            testid="hero-add-participant-phone"
            hint="Optional"
          />
        </div>

        {error && (
          <p
            role="alert"
            className="mt-3 text-[12px] text-rose-600 leading-snug"
            data-testid="hero-add-participant-error"
          >
            {error}
          </p>
        )}

        <footer className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            className="h-9 px-3.5 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-[12.5px] font-semibold text-slate-700 transition disabled:opacity-50"
            data-testid="hero-add-participant-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12.5px] font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="hero-add-participant-submit"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Add {slot.label.toLowerCase()}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}

/* ─── Compact form field ─────────────────────────────────────────── */
function Field({ label, value, onChange, placeholder, type = "text", required, testid, hint, inputRef }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between mb-1">
        <span className="text-[11px] uppercase tracking-[0.10em] text-slate-500 font-semibold">
          {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
        </span>
        {hint && <span className="text-[10.5px] text-slate-400">{hint}</span>}
      </span>
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full h-9 px-3 rounded-lg bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-300 outline-none text-[13px] text-slate-900 placeholder:text-slate-400 transition"
        data-testid={testid}
      />
    </label>
  );
}

export default HeroParticipantsPanel;
